///
/// Valid & Approved
///

import { AxiosError, AxiosResponse, isAxiosError } from 'axios'

export interface ValidationFailedResponse extends AxiosResponse {
  data: {
    code: 'rest_invalid_param'
    data: {
      params: Record<string, string>
    }
    message: string
    status: 400
  }
}

export interface ValidationFailedError extends AxiosError {
  response: ValidationFailedResponse
}

function axiosResponseIsValidationFailedResponse(response: AxiosResponse): response is ValidationFailedResponse {
  return (
    response.status === 400 &&
    typeof response.data?.message === 'string' &&
    typeof response.data?.data?.params === 'object'
  )
}

export function isValidationFailedResponse(error: unknown): error is ValidationFailedError {
  return Boolean(isAxiosError(error) && error.response && axiosResponseIsValidationFailedResponse(error.response))
}

export interface LatLng {
  lat: number | string
  lng: number | string
}

export type PassengerTypes = 'adults' | 'children' | 'infants'

export type PassengerRequirements = Record<PassengerTypes, number>

export type LuggageTypes = 'luggage_small' | 'luggage_large'

export type LuggageRequirements = Record<LuggageTypes, number>

export type OversizeLuggageTypes =
  | 'oversized_skis'
  | 'oversized_snowboards'
  | 'oversized_golf_clubs'
  | 'oversized_prams'
  | 'oversized_child_seats'

export type OversizeLuggageRequirements = Record<OversizeLuggageTypes, number>

export enum TransferDirection {
  ToAirport = 'to-airport',
  ToAddress = 'to-address',
}

export enum TransferProvider {
  Bespoke = 'bespoke',
  Hoppa = 'hoppa',
}

export enum TransferStatus {
  Pending = 'pending',
  Reserved = 'reserved',
  Confirmed = 'confirmed',
  Cancelled = 'cancelled',
  Failed = 'failed',
}

export interface AuthenticatedUser {
  display_name: string
  email: string
  first_name: string
  last_name: string
  phone_number: string
  profile_img_url: string | null
}

export interface Address {
  // Google Place ID
  address_place_id: string
  address_name: string
  address_formatted: string
  address_components: google.maps.GeocoderAddressComponent[] // TODO: Check the result of this.
  address_country: string
  address_lat: number | string
  address_lng: number | string
}

export interface Airport {
  airport_name: string
  airport_code: string
  airport_country: string
  airport_lat: number | string
  airport_lng: number | string
}

export interface ConnectingAirport {
  connecting_airport_name: string
  connecting_airport_code: string
  connecting_airport_country: string
  connecting_airport_lat: number | string
  connecting_airport_lng: number | string
}

export type CoreTransferInformation = {
  id?: string
  order_id?: string
  provider?: string
  status?: TransferStatus
  booking_id: string
  session_id: string
  direction: TransferDirection
  pickup_datetime: string
  flight_number: string

  refund_amount?: number
  total: number
}

export interface ContactInformation {
  contact_first_name: string
  contact_last_name: string
  contact_email: string
  contact_phone?: string
}

export interface SupplierInformation {
  supplier_name: string
  supplier_phone_number: string
  supplier_notes: string
}

export type Transfer = CoreTransferInformation &
  Partial<ContactInformation> &
  Partial<SupplierInformation> &
  Airport &
  ConnectingAirport &
  Address &
  PassengerRequirements &
  LuggageRequirements &
  OversizeLuggageRequirements

export interface Order {
  id: number
  status: string
  total: number
  customer_first_name: string
  customer_last_name: string
  customer_phone: string
  customer_email: string
  payment_status: string // TODO: Likely can be an Enum
  created_at: string
  updated_at: string
  transfers: Transfer[]
}
